import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import TickerTimeout from "../../../utils/TickerTimeout";
import { Settings } from "../../../settings";

// Static mode configuration
export const STATIC_CONFIG = {
  widthMultiplier: 1,
  heightMultiplier: 1,
  xOffset: 0,
  yOffset: 0,
};

// Spine mode configuration
export const SPINE_CONFIG = {
  yOffset: -0.004,
  xOffset: 0,
  widthMultiplier: 1.155275,
  heightMultiplier: 1.202,
};

// Manages spine animation for ReelBack component
class ReelBackController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
  ) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.spine = null;

    // Internal caches for performance optimization
    this._availableAnimationsCache = null;
    this._dividerCountCache = Settings.REEL_COUNT - 1;

    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpineAnimation();
    });
  }

  // Initialize spine and play background animation
  initializeSpineAnimation() {
    this.spine = this.createSpine();

    if (!this.spine) {
      return;
    }

    // VALIDATION: Ensure spine has all required components
    const missingComponents = [];
    if (!this.spine.skeleton) missingComponents.push("skeleton");
    if (!this.spine.state) missingComponents.push("state");
    if (!this.spine.skeleton?.data) missingComponents.push("skeleton.data");
    if (!this.spine.skeleton?.slots) missingComponents.push("skeleton.slots");
    if (!this.spine.skeleton?.bones) missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Setup spine
    this.spine.position.set(0, 0);
    this.spine.zIndex = 2;
    this.spine.visible = true;

    // Add to sprite container
    this.container.addChild(this.spine);

    // VALIDATION: Final check that spine is properly attached
    if (this.spine.parent !== this.container) {
      return;
    }

    // Invalidate animations cache when spine is reinitialized
    this._availableAnimationsCache = null;

    // Apply scaling to match sprite dimensions
    this.applySpineScaling();

    // Play the reel_back animation
    this.playAnimation("reel_back", true);
  }

  // Create and verify spine instance
  createSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "reel_background-skeleton";
      const atlasAlias = "reel_background-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      // Create independent spine instance
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      // Verify skeleton has required data
      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  // Play animation by name on track 0
  playAnimation(animationName, loop = false) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.spine.skeleton.data.animations;
    const animation = animations.find((anim) => anim.name === animationName);

    if (!animation) {
      return;
    }

    this.spine.state.setAnimation(0, animationName, loop);
  }

  // Scale spine to match reel area dimensions
  applySpineScaling() {
    if (!this.spine || !this.symbolHeight || !this.dimensions) {
      return;
    }

    const spineBounds = this.spine.getLocalBounds();
    if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
      new TickerTimeout(() => {
        this.applySpineScaling();
      }, 100);
      return;
    }

    // Target inner frame size that should align with the reel area
    // Use cached divider count
    const dividerWidth = this.dividerDimensions?.width ?? 0;
    // Intentionally reduce by two divider widths (overflow on the sides)
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.widthMultiplier +
      dividerWidth * (this._dividerCountCache - 2);
    const targetFrameHeight =
      this.symbolHeight *
      Settings.SYMBOLS_VISIBLE *
      SPINE_CONFIG.heightMultiplier;

    const scaleX = targetFrameWidth / spineBounds.width;
    const scaleY = targetFrameHeight / spineBounds.height;
    this.spine.scale.set(scaleX, scaleY);

    this.spine.position.set(0, 0);
  }

  // Update dimensions and reapply scaling
  resizeReelBack(
    newSymbolWidth,
    newSymbolHeight,
    newDimensions,
    newDividerDimensions,
  ) {
    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;
    if (newDimensions) {
      this.dimensions = newDimensions;
    }
    if (newDividerDimensions) {
      this.dividerDimensions = newDividerDimensions;
    }
    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Get list of available animation names
  // Cached for performance - invalidated when spine is reinitialized
  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) {
      return [];
    }

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.spine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  // Set width multiplier and reapply scaling
  setWidthMultiplier(multiplier) {
    this.widthMultiplier = multiplier;
    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Set height multiplier and reapply scaling
  setHeightMultiplier(multiplier) {
    this.heightMultiplier = multiplier;
    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Set general scale multiplier and reapply scaling
  setGeneralScaleMultiplier(multiplier) {
    this.generalScaleMultiplier = multiplier;
    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Set multiple multipliers at once and reapply scaling
  setMultipliers({ width, height, scale }) {
    if (width !== undefined) this.widthMultiplier = width;
    if (height !== undefined) this.heightMultiplier = height;
    if (scale !== undefined) this.generalScaleMultiplier = scale;

    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Get current multiplier values
  getMultipliers() {
    return {
      width: this.widthMultiplier,
      height: this.heightMultiplier,
      scale: this.generalScaleMultiplier,
    };
  }
}

export default ReelBackController;
